import { type FC, useMemo } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import StatCard from '@/components/base/StatCard';
import PriorityBadge from '@/components/base/PriorityBadge';
import { useDashboard } from '@/hooks/useDashboard';

import {
  classifyOperationalDeadline,
  getLocalDateOnly,
} from '@/lib/caseDeadlineOverview';

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const formatCurrency = (value: number) => currencyFormatter.format(value);

const formatDate = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value.includes('T') ? value : `${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString('pt-BR');
};

const formatDateTime = (value: string | null | undefined) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getLeadStatusLabel = (status: string) =>
  ({
    new: 'Novo',
    contacted: 'Em contato',
    proposal_sent: 'Proposta enviada',
    hired: 'Contratado',
    lost: 'Perdido',
    archived: 'Arquivado',
  })[status] ?? status;

const getCaseStatusLabel = (status: string) =>
  ({
    active: 'Ativo',
    waiting_client: 'Aguardando cliente',
    waiting_court: 'Aguardando juízo',
    hearing_scheduled: 'Audiência marcada',
    completed: 'Concluído',
    archived: 'Arquivado',
  })[status] ?? status;

const getTaskStatusLabel = (status: string) =>
  ({
    pending: 'A fazer',
    in_progress: 'Em andamento',
    completed: 'Concluída',
    cancelled: 'Cancelada',
    archived: 'Arquivada',
  })[status] ?? status;

const getPaymentStatusLabel = (status: string) =>
  ({
    pending: 'Pendente',
    active: 'Ativo',
    paid: 'Pago',
    overdue: 'Vencido',
    cancelled: 'Cancelado',
    archived: 'Arquivado',
  })[status] ?? status;

const getLeadStatusClass = (status: string) =>
  ({
    new: 'bg-blue-50 text-blue-700 border-blue-200',
    contacted: 'bg-amber-50 text-amber-700 border-amber-200',
    proposal_sent: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    hired: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    lost: 'bg-red-50 text-red-700 border-red-200',
    archived: 'bg-gray-100 text-gray-500 border-gray-200',
  })[status] ?? 'bg-gray-50 text-gray-600 border-gray-200';

const getCaseStatusClass = (status: string) =>
  ({
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    waiting_client: 'bg-amber-50 text-amber-700 border-amber-200',
    waiting_court: 'bg-orange-50 text-orange-700 border-orange-200',
    hearing_scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    completed: 'bg-gray-50 text-gray-600 border-gray-200',
    archived: 'bg-gray-100 text-gray-500 border-gray-200',
  })[status] ?? 'bg-gray-50 text-gray-600 border-gray-200';

const getTaskStatusClass = (status: string) =>
  ({
    pending: 'bg-gray-50 text-gray-600 border-gray-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',
    archived: 'bg-gray-100 text-gray-500 border-gray-200',
  })[status] ?? 'bg-gray-50 text-gray-600 border-gray-200';

const getPaymentStatusClass = (status: string) =>
  ({
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    active: 'bg-blue-50 text-blue-700 border-blue-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    overdue: 'bg-red-50 text-red-700 border-red-200',
    cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
    archived: 'bg-gray-100 text-gray-500 border-gray-200',
  })[status] ?? 'bg-gray-50 text-gray-600 border-gray-200';

const isTaskOverdue = (dueDate: string | null) => {
  if (!dueDate) {
    return false;
  }

  const parsed = new Date(dueDate.includes('T') ? dueDate : `${dueDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed < today;
};

const isCaseDeadlineSoon = (deadline: string | null) => {
  if (!deadline) {
    return false;
  }

  const parsed = new Date(deadline.includes('T') ? deadline : `${deadline}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sevenDaysAhead = new Date(today);
  sevenDaysAhead.setDate(sevenDaysAhead.getDate() + 7);

  return parsed >= today && parsed <= sevenDaysAhead;
};

const getDashboardDeadlineTimingLabel = (
  dueDate:
    string |
    null,

  status:
    string,

  riskLevel:
    string
) => {
  const timing =
    classifyOperationalDeadline(
      {
        dueDate,
        status,
        riskLevel,
      },
      getLocalDateOnly()
    );

  if (
    timing.state ===
    'overdue'
  ) {
    const days =
      Math.abs(
        timing.daysUntil ??
        0
      );

    return days ===
      1
      ? 'Vencido há 1 dia'
      : `Vencido há ${days} dias`;
  }

  if (
    timing.state ===
    'today'
  ) {
    return 'Vence hoje';
  }

  if (
    timing.daysUntil ===
    1
  ) {
    return 'Vence amanhã';
  }

  if (
    timing.daysUntil !==
    null
  ) {
    return `Vence em ${timing.daysUntil} dias`;
  }

  return 'Sem data';
};

const getDashboardDeadlineTimingClass = (
  dueDate:
    string |
    null,

  status:
    string,

  riskLevel:
    string
) => {
  const timing =
    classifyOperationalDeadline(
      {
        dueDate,
        status,
        riskLevel,
      },
      getLocalDateOnly()
    );

  if (
    timing.state ===
    'overdue'
  ) {
    return 'bg-red-50 text-red-700 border-red-200';
  }

  if (
    timing.state ===
    'today'
  ) {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }

  if (
    timing.daysUntil !==
      null &&
    timing.daysUntil <=
      3
  ) {
    return 'bg-orange-50 text-orange-700 border-orange-200';
  }

  return 'bg-blue-50 text-blue-700 border-blue-200';
};
const Dashboard: FC = () => {
  const { dashboard, isLoadingDashboard, dashboardError, loadDashboard } = useDashboard();

  const leadSourceTotal = useMemo(
    () => dashboard.leadSources.reduce((sum, item) => sum + item.count, 0),
    [dashboard.leadSources]
  );

  const leadSourcesWithPercent = useMemo(
    () =>
      dashboard.leadSources.map((item) => ({
        ...item,
        percentage: leadSourceTotal > 0 ? Math.round((item.count / leadSourceTotal) * 100) : 0,
      })),
    [dashboard.leadSources, leadSourceTotal]
  );

  const stats = useMemo(
    () => [
      {
        id: 'active-clients',
        label: 'Clientes Ativos',
        value: dashboard.summary.activeClients,
        change: 'Visão geral do escritório',
        changeType: 'neutral' as const,
        icon: 'ri-group-line',
        variant: 'default' as const,
      },
      {
        id: 'active-leads',
        label: 'Leads Ativos',
        value: dashboard.summary.activeLeads,
        change: 'Clientes e leads',
        changeType: dashboard.summary.activeLeads > 0 ? ('positive' as const) : ('neutral' as const),
        icon: 'ri-user-3-line',
        variant: 'default' as const,
      },
      {
        id: 'active-cases',
        label: 'Casos Ativos',
        value: dashboard.summary.activeCases,
        change:
          dashboard.summary.overdueDeadlines > 0
            ? `${dashboard.summary.overdueDeadlines} prazo(s) vencido(s)`
            : dashboard.summary.todayDeadlines > 0
              ? `${dashboard.summary.todayDeadlines} vencendo hoje`
              : `${dashboard.summary.upcomingDeadlines} nos próximos 7 dias`,
        changeType: dashboard.summary.activeCases > 0 ? ('positive' as const) : ('neutral' as const),
        icon: 'ri-briefcase-4-line',
        variant: 'default' as const,
      },
      {
        id: 'pending-tasks',
        label: 'Tarefas Pendentes',
        value: dashboard.summary.pendingTasks,
        change: 'Distribuição de tarefas',
        changeType: dashboard.summary.pendingTasks > 0 ? ('neutral' as const) : ('positive' as const),
        icon: 'ri-todo-line',
        variant: 'default' as const,
      },
    ],
    [dashboard]
  );

  const activeLeadList = dashboard.recentLeads.slice(0, 5);
  const recentClientList = dashboard.recentClients.slice(0, 5);
  const upcomingDeadlineList = dashboard.upcomingDeadlines.slice(0, 5);
  const activeCaseList = dashboard.activeCases.slice(0, 5);
  const taskList = dashboard.pendingTasks.slice(0, 5);
  const installmentList = dashboard.upcomingInstallments.slice(0, 5);
  const recentPaymentList = dashboard.recentPayments.slice(0, 5);

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Dashboard</h2>
            <p className="text-sm text-foreground-500 mt-0.5">
              Visão geral do escritório
            </p>
          </div>
          <div className="text-xs text-foreground-500">
            {isLoadingDashboard ? 'Carregando visão geral do escritório...' : `Atualizado em ${formatDateTime(dashboard.generatedAt)}`}
          </div>
        </div>

        {dashboardError && (
          <div className="flex items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <i className="ri-alert-line"></i>
              <span>{dashboardError}</span>
            </div>
            <button
              onClick={() => void loadDashboard()}
              className="text-xs font-medium text-amber-700 hover:text-amber-800 cursor-pointer whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!dashboard.aggregation.complete && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700" role="status">
            <i className="ri-error-warning-line"></i>
            <span>{dashboard.aggregation.warning ?? 'Não foi possível consolidar todas as fontes. Os indicadores podem estar parciais.'}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {stats.map((stat) => (
            <StatCard
              key={stat.id}
              label={stat.label}
              value={stat.value}
              change={stat.change}
              changeType={stat.changeType}
              icon={stat.icon}
              variant={stat.variant}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <section className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground-900">Comercial</h3>
              <span className="text-xs text-foreground-400">Clientes e leads</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Leads por Origem</h4>
                <div className="space-y-2">
                  {leadSourcesWithPercent.length > 0 ? (
                    leadSourcesWithPercent.map((item) => (
                      <div key={item.source} className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0"></div>
                          <span className="text-sm text-foreground-700 truncate">{item.source}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-semibold text-foreground-800">{item.count}</span>
                          <span className="text-xs text-foreground-400">{item.percentage}%</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">Nenhum lead ativo encontrado.</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Leads Recentes</h4>
                <div className="space-y-2.5">
                  {activeLeadList.length > 0 ? (
                    activeLeadList.map((lead) => (
                      <div key={lead.id} className="p-2.5 bg-background-100 rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground-800 truncate">{lead.name}</p>
                          <PriorityBadge priority={lead.priority === 'urgent' ? 'Urgente' : lead.priority === 'high' ? 'Alta' : lead.priority === 'low' ? 'Baixa' : 'Normal'} />
                        </div>
                        <p className="text-xs text-foreground-500 truncate">{lead.legalProductName ?? 'Sem produto jurídico vinculado'}</p>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${getLeadStatusClass(lead.status)}`}>
                            {getLeadStatusLabel(lead.status)}
                          </span>
                          <span className="text-[10px] text-foreground-400">{formatDate(lead.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">Nenhum lead ativo encontrado.</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Clientes Recentes</h4>
                <div className="space-y-2.5">
                  {recentClientList.length > 0 ? (
                    recentClientList.map((client) => (
                      <div key={client.id} className="p-2.5 bg-background-100 rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground-800 truncate">{client.name}</p>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full border font-medium bg-emerald-50 text-emerald-700 border-emerald-200">
                            {client.status === 'active' ? 'Ativo' : client.status === 'inactive' ? 'Inativo' : client.status ?? 'Sem status'}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-500 truncate">{client.city ?? 'Sem cidade informada'}</p>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] text-foreground-400">{client.responsibleUser ?? 'Sem responsável'}</span>
                          <span className="text-[10px] text-foreground-400">{formatDate(client.createdAt)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">Nenhum cliente recente encontrado.</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground-900">Jurídica</h3>
              <span className="text-xs text-foreground-400">Prazos, tarefas e casos</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
                  Prazos Prioritários
                </h4>

                <div className="space-y-2.5">
                  {upcomingDeadlineList.length > 0 ? (
                    upcomingDeadlineList.map((item) => (
                      <div
                        key={item.id}
                        className="p-2.5 bg-background-100 rounded-lg space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground-800 truncate">
                            {item.title}
                          </p>

                          <PriorityBadge
                            priority={
                              item.riskLevel ===
                              'high'
                                ? 'Alta'
                                : item.riskLevel ===
                                  'low'
                                  ? 'Baixa'
                                  : 'Normal'
                            }
                          />
                        </div>

                        <p className="text-xs text-foreground-500 truncate">
                          {item.clientName ??
                            'Sem cliente vinculado'}

                          {item.caseTitle
                            ? ` · ${item.caseTitle}`
                            : ''}
                        </p>

                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${getDashboardDeadlineTimingClass(
                              item.dueDate,
                              item.status,
                              item.riskLevel
                            )}`}
                          >
                            {getDashboardDeadlineTimingLabel(
                              item.dueDate,
                              item.status,
                              item.riskLevel
                            )}
                          </span>

                          <span className="text-[10px] text-foreground-400">
                            {formatDate(
                              item.dueDate
                            )}
                          </span>
                        </div>

                        {item.responsibleUser && (
                          <p className="text-[10px] text-foreground-400 truncate">
                            Responsável: {item.responsibleUser}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">
                      Nenhum prazo operacional ativo encontrado.
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Tarefas Pendentes / Vencidas</h4>
                <div className="space-y-2.5">
                  {taskList.length > 0 ? (
                    taskList.map((task) => (
                      <div key={task.id} className="p-2.5 bg-background-100 rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground-800 truncate">{task.title}</p>
                          <PriorityBadge priority={task.priority === 'urgent' ? 'Urgente' : task.priority === 'high' ? 'Alta' : task.priority === 'low' ? 'Baixa' : 'Normal'} />
                        </div>
                        <p className="text-xs text-foreground-500 truncate">
                          {task.clientName ?? 'Sem cliente'}{task.caseTitle ? ` · ${task.caseTitle}` : ''}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${getTaskStatusClass(task.status)}`}>
                            {getTaskStatusLabel(task.status)}
                          </span>
                          <span className="text-[10px] text-foreground-400">{formatDate(task.dueDate)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">Nenhuma tarefa pendente.</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Casos em Andamento</h4>
                <div className="space-y-2.5">
                  {activeCaseList.length > 0 ? (
                    activeCaseList.map((item) => (
                      <div key={item.id} className="p-2.5 bg-background-100 rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground-800 truncate">{item.clientName ?? item.title}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${getCaseStatusClass(item.status)}`}>
                            {getCaseStatusLabel(item.status)}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-500 truncate">{item.legalProductName ?? 'Sem produto jurídico vinculado'}</p>
                        <div className="flex items-center justify-between gap-2">
                          <PriorityBadge priority={item.priority === 'urgent' ? 'Urgente' : item.priority === 'high' ? 'Alta' : item.priority === 'low' ? 'Baixa' : 'Normal'} />
                          <span className="text-[10px] text-foreground-400">{formatDate(item.nextDeadlineAt)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">Nenhum caso em andamento encontrado.</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground-900">Financeira</h3>
              <span className="text-xs text-foreground-400">Controle financeiro</span>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              {[
                { label: 'Receita contratada', value: formatCurrency(dashboard.summary.contractedRevenue), color: 'bg-primary-500' },
                { label: 'Receita recebida', value: formatCurrency(dashboard.summary.receivedRevenue), color: 'bg-emerald-500' },
                { label: 'Valores a receber', value: formatCurrency(dashboard.summary.pendingRevenue), color: 'bg-amber-500' },
                { label: 'Vencidas', value: formatCurrency(dashboard.summary.overdueRevenue), color: 'bg-red-500' },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-background-100 rounded-lg">
                  <div className={`w-full h-1.5 rounded-full ${item.color} mb-2 opacity-30`}></div>
                  <p className="text-[10px] text-foreground-500 font-medium uppercase tracking-wider">{item.label}</p>
                  <p className="text-sm font-bold text-foreground-900 mt-0.5">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Parcelas a Vencer</h4>
                <div className="space-y-2.5">
                  {installmentList.length > 0 ? (
                    installmentList.map((item) => (
                      <div key={item.id} className="p-2.5 bg-background-100 rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground-800 truncate">{item.clientName ?? item.paymentTitle}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${getPaymentStatusClass(item.paymentStatus)}`}>
                            {getPaymentStatusLabel(item.paymentStatus)}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-500 truncate">
                          {item.caseTitle ?? 'Sem caso vinculado'} · {item.paymentTitle}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{formatCurrency(item.amount)}</span>
                          <span className="text-[10px] text-foreground-400">{formatDate(item.dueDate)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">Nenhuma parcela pendente ou vencida.</p>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Últimos Pagamentos</h4>
                <div className="space-y-2.5">
                  {recentPaymentList.length > 0 ? (
                    recentPaymentList.map((payment) => (
                      <div key={payment.id} className="p-2.5 bg-background-100 rounded-lg space-y-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium text-foreground-800 truncate">{payment.clientName ?? payment.title}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${getPaymentStatusClass(payment.status)}`}>
                            {getPaymentStatusLabel(payment.status)}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-500 truncate">
                          {payment.caseTitle ?? 'Sem caso vinculado'} · {payment.title}
                        </p>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">
                            {formatCurrency(payment.totalAmount)}
                          </span>
                          <span className="text-[10px] text-foreground-400">
                            Saldo {formatCurrency(payment.balanceAmount ?? 0)}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-foreground-400">Nenhum pagamento recente encontrado.</p>
                  )}
                </div>
              </div>
            </div>
          </section>

          <section className="bg-background-50 border border-background-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground-900">Produtividade</h3>
              <span className="text-xs text-foreground-400">Distribuição de tarefas</span>
            </div>

            <div className="space-y-4">
              {dashboard.tasksByResponsible.length > 0 ? (
                dashboard.tasksByResponsible.map((person) => {
                  const percent = person.total > 0 ? Math.round((person.completed / person.total) * 100) : 0;

                  return (
                    <div key={person.name}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-sm font-medium text-foreground-700">{person.name}</span>
                        <span className="text-xs text-foreground-400">
                          {person.completed}/{person.total} concluídas
                        </span>
                      </div>
                      <div className="w-full h-2 bg-background-100 rounded-full overflow-hidden">
                        <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${percent}%` }}></div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-foreground-400">Concluídas</span>
                        <span className="text-[10px] font-semibold text-primary-600">{percent}%</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-xs text-foreground-400">Nenhuma tarefa atribuída ainda.</p>
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-background-200 grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{dashboard.summary.completedTasks}</p>
                <p className="text-[10px] text-foreground-500">Concluídas</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-red-500">{dashboard.summary.overdueTasks}</p>
                <p className="text-[10px] text-foreground-500">Vencidas</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-bold text-foreground-900">{dashboard.summary.pendingTasks}</p>
                <p className="text-[10px] text-foreground-500">Pendentes</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
